import { useMemo } from "react";
import PropTypes from "prop-types";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import LinearProgress from "@mui/material/LinearProgress";

import { get, put } from "lib/api";
import { useAuth } from "context/AuthContext";
import MobileAddressManager from "mobile/components/MobileAddressManager";
import MobileTopBar from "mobile/components/MobileTopBar";
import { IosCard, IosPage } from "mobile/components/IosParity";

const normalize = (d) =>
  d?.attributes ? { id: d.id, documentId: d.documentId || d.id, ...d.attributes } : { ...d, documentId: d.documentId || d.id };

function Row({ label, value }) {
  return (
    <Stack direction="row" justifyContent="space-between" spacing={2}>
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 110 }}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={800} sx={{ textAlign: "right", wordBreak: "break-word" }}>
        {value || "—"}
      </Typography>
    </Stack>
  );
}

Row.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
};
Row.defaultProps = {
  value: "—",
};

export default function VendorDetailMobile() {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { activeCompany } = useAuth();

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ["m-vendor", documentId],
    queryFn: () => get(`/api/vendors/${documentId}`, { populate: "*" }),
    enabled: !!documentId,
  });

  const vendor = useMemo(() => (data?.data ? normalize(data.data) : null), [data]);

  const emails = Array.isArray(vendor?.emails) ? vendor.emails.filter(Boolean) : [];
  const phones = Array.isArray(vendor?.phones) ? vendor.phones.filter(Boolean) : [];
  const addresses = Array.isArray(vendor?.addresses) ? vendor.addresses : [];
  const saveAddressesMutation = useMutation({
    mutationFn: (nextAddresses) => put(`/api/vendors/${vendor?.documentId || documentId}`, { data: { addresses: nextAddresses } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["m-vendor", documentId] }),
  });

  return (
    <IosPage>
      <MobileTopBar
        title={vendor?.name || "Vendor"}
        subtitle={vendor?.vendorNumber ? `Vendor ${vendor.vendorNumber}` : "Details"}
        onBack={() => navigate("/m/vendors")}
        right={
          <IconButton aria-label="Refresh" size="small" onClick={() => qc.invalidateQueries({ queryKey: ["m-vendor", documentId] })}>
            <Icon fontSize="small">refresh</Icon>
          </IconButton>
        }
      />

      {(isLoading || isFetching) && <LinearProgress sx={{ mt: 1 }} />}

      {error ? (
        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
          {error?.response?.status === 404 ? "Vendor not found." : error?.message || "Error loading vendor."}
        </Typography>
      ) : null}

      {vendor ? (
        <Stack spacing={0.75} sx={{ mt: 1 }}>
          <IosCard>
            <Stack spacing={0.75}>
              <Typography variant="subtitle2" fontWeight={900} noWrap>
                {vendor.name}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {vendor.contact || "—"}
              </Typography>

              <Divider />

              <Row label="Vendor #" value={vendor.vendorNumber || "—"} />
              <Row label="Contact" value={vendor.contact || "—"} />
              <Row label="Emails" value={emails.length ? emails.join(", ") : "—"} />
              <Row label="Phones" value={phones.length ? phones.join(", ") : "—"} />
              <Row label="Payment" value={vendor.paymentTerms || "—"} />
              <Row label="Tax ID" value={vendor.taxId || "—"} />
              <Row label="Notes" value={vendor.notes || "—"} />
            </Stack>
          </IosCard>

          <MobileAddressManager
            addresses={addresses}
            companyKey={activeCompany}
            disabled={saveAddressesMutation.isPending}
            onSave={(nextAddresses) => saveAddressesMutation.mutateAsync(nextAddresses)}
          />
        </Stack>
      ) : null}
    </IosPage>
  );
}
